/** biome-ignore-all lint/suspicious/noExplicitAny: <> */
import { dt } from "./date";

/**
 * Transforme un objet en une chaîne de query params utilisable dans une URL.
 * Filtre les valeurs `null` ou `undefined`.
 * @param data - L'objet à transformer
 * @returns string - Chaîne de query params sérialisée
 */
export function serializeQueryParams<T extends Record<string, any>>(data: T): string {
    const params = new URLSearchParams();

    Object.entries(data).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;

        if (Array.isArray(value)) {
            const allDates = value.every((item) => item instanceof Date);

            value.forEach((item) => {
                const serializedValue = allDates ? dt.toIsoDateTime(item) : String(item);
                params.append(key, serializedValue);
            });

            return;
        }

        if (value instanceof Date) {
            params.append(key, dt.toIsoDateTime(value));
            return;
        }

        if (typeof value === "object") {
            params.append(key, JSON.stringify(value));
            return;
        }

        params.append(key, String(value));
    });

    return params.toString();
}

export interface ParamParser<T = any> {
    parse: (value: string[]) => T | undefined;
}

export const StringParser: ParamParser<string> = {
    parse: (values) => (values.length > 0 ? values[0] : undefined),
};

export const NumberParser: ParamParser<number> = {
    parse: (values) => (values.length > 0 ? Number(values[0]) : undefined),
};

export const BooleanParser: ParamParser<boolean> = {
    parse: (values) => (values.length > 0 ? values[0] === "true" : undefined),
};

export const DateParser: ParamParser<Date> = {
    parse: (values) => {
        if (values.length === 0) return undefined;

        const date = dt.fromISO(values[0]);
        return Number.isNaN(date.getTime()) ? undefined : date;
    },
};

export const ObjectParser: ParamParser<Record<string, any>> = {
    parse: (values) => {
        if (values.length === 0) return undefined;

        try {
            return JSON.parse(values[0]);
        } catch {
            return undefined;
        }
    },
};

export const ArrayParser = <T>(itemParser: ParamParser<T>): ParamParser<T[]> => ({
    parse: (values) => {
        if (!values.length) return undefined;
        return values.map((v) => itemParser.parse([v])).filter((item) => item !== undefined) as T[];
    },
});

// Enum
export const EnumParser = <T extends Record<string, string>>(
    enumObject: T,
): ParamParser<T[keyof T]> => ({
    parse: (values) => {
        if (values.length === 0) return undefined;

        const firstValue = values[0];
        return Object.values(enumObject).includes(firstValue as T[keyof T])
            ? (firstValue as T[keyof T])
            : undefined;
    },
});

type ParsedParams<T extends Record<string, ParamParser<any>>> = {
    [K in keyof T]: T[K] extends ParamParser<infer U> ? U | undefined : never;
};

export function parseQueryParams<T extends Record<string, ParamParser<any>>>(
    query: string | URLSearchParams,
    paramParsers: T,
): ParsedParams<T> {
    const urlParams = new URLSearchParams(query);
    const result = {} as ParsedParams<T>;

    Object.entries(paramParsers).forEach(([key, parser]) => {
        const values = urlParams.getAll(key);

        if (values.length === 0) return;

        result[key as keyof T] = parser.parse(values);
    });

    return result;
}
